// sheetPlans.js
// A person's plan of one sheet of a workbook (Spec 2 section 3.2), the value of
// the sheet's Segment decision, and the checks it must pass before it is saved:
//
//     plan = { fields: { 'Reporting Month': 'Jan 2026' },
//              regions: [{ region_id, name, header_first_row, header_last_row,
//                          body_first_row, body_last_row, col_first, col_last,
//                          exclude_rows, section_values, unpivot, approximate }] }
//
// The sheet workspace lists these problems beneath its rail, with the checks
// only it can make, and keeps Save disabled while there are any; the Segment
// edit gesture refuses a plan with the first of them. The backend's
// sheet_discovery.validate_person_plan makes the same checks against the sheet
// itself, in the same sentences, and refuses the plan with a dropped note when
// one fails.

// At most four header rows (sheet_discovery.MAX_HEADER_ROWS).
export const MAX_HEADER_ROWS = 4;

// A region id is uuid4 hex, as discovery and the workspace mint them. A
// column is named in capital letters, as the workspace writes it.
const REGION_ID_PATTERN = /^[0-9a-f]{32}$/;
const COLUMN_LETTERS_PATTERN = /^[A-Z]+$/;

// Spreadsheet column letters and their 1-based numbers, as openpyxl's
// get_column_letter and column_index_from_string: 1 is A, 27 is AA. A number
// that is not a column gives '', and text that is not column letters gives null.
export function columnIndexToLetter(index) {
  let number = Number(index);
  if (!Number.isFinite(number) || number < 1) return '';
  let letters = '';
  while (number > 0) {
    const remainder = (number - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    number = Math.floor((number - 1) / 26);
  }
  return letters;
}

export function columnLetterToIndex(letters) {
  const text = String(letters ?? '').trim().toUpperCase();
  if (!/^[A-Z]+$/.test(text)) return null;
  let number = 0;
  for (const letter of text) number = number * 26 + (letter.charCodeAt(0) - 64);
  return number;
}

// Every reason the plan cannot be saved, in the order validate_person_plan
// meets them; empty when it can be. Each table keeps a valid id of its own, its
// header rows sit above its body rows and number at most four, its columns run
// left to right, and it keeps at least one body row; no two tables' BODIES
// share cells (a shared header band is allowed: the parts of a split region
// share their header rows).
export function sheetPlanProblems(plan) {
  const isMap = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  if (!isMap(plan) || !isMap(plan.fields) || !Array.isArray(plan.regions)) {
    return ['A sheet plan needs its header fields and its list of tables.'];
  }

  const problems = [];
  const regionIds = new Set();
  // The tables whose bounds are whole, for the overlap check below.
  const bodies = [];
  plan.regions.forEach((region, index) => {
    const label = `Table ${index + 1}`;
    const regionId = isMap(region) ? region.region_id : undefined;
    if (typeof regionId !== 'string' || !REGION_ID_PATTERN.test(regionId)) {
      problems.push(`${label} has no valid id. Remove it and draw it again.`);
      return;
    }
    if (regionIds.has(regionId)) {
      problems.push(`${label} has the same id as another table. Remove it and draw it again.`);
      return;
    }
    regionIds.add(regionId);

    const rows = [region.header_first_row, region.header_last_row, region.body_first_row, region.body_last_row];
    const rowsAreWhole = rows.every((row) => Number.isInteger(row) && row >= 1);
    const columnsAreLetters = [region.col_first, region.col_last].every((column) => (
      typeof column === 'string' && COLUMN_LETTERS_PATTERN.test(column)
    ));
    if (!rowsAreWhole || !columnsAreLetters) {
      problems.push(`${label} is not a complete table: it needs header rows, body rows and columns.`);
      return;
    }
    if (region.header_last_row < region.header_first_row || region.body_first_row <= region.header_last_row) {
      problems.push(`${label} must have its header rows above its body rows.`);
      return;
    }
    if (region.header_last_row - region.header_first_row + 1 > MAX_HEADER_ROWS) {
      problems.push(`${label} has more than ${MAX_HEADER_ROWS} header rows.`);
      return;
    }
    const firstColumn = columnLetterToIndex(region.col_first);
    const lastColumn = columnLetterToIndex(region.col_last);
    if (firstColumn > lastColumn) {
      problems.push(`${label} must have its first column before its last.`);
      return;
    }

    const excludedInBody = new Set((region.exclude_rows || []).filter((row) => (
      row >= region.body_first_row && row <= region.body_last_row
    )));
    const bodyRows = region.body_last_row - region.body_first_row + 1 - excludedInBody.size;
    if (bodyRows <= 0) {
      problems.push(`${label} has no rows. Adjust its bounds, or remove it.`);
      return;
    }
    bodies.push({
      position: index + 1,
      top: region.body_first_row,
      bottom: region.body_last_row,
      left: firstColumn,
      right: lastColumn,
    });
  });

  bodies.forEach((first, firstIndex) => {
    bodies.slice(firstIndex + 1).forEach((second) => {
      const sharedFirstRow = Math.max(first.top, second.top);
      const sharedLastRow = Math.min(first.bottom, second.bottom);
      const columnsMeet = !(first.right < second.left || second.right < first.left);
      if (sharedFirstRow <= sharedLastRow && columnsMeet) {
        problems.push(`Tables ${first.position} and ${second.position} share rows `
          + `${sharedFirstRow}–${sharedLastRow}; move or shrink one of them.`);
      }
    });
  });
  return problems;
}
